import React, { useState } from 'react';
import { useStyles } from './styles';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import ConsentChip, { ConsentChipStyleEnum } from '../../components/common/ConsentChip';
import DropDown from '../../components/common/dropdown';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import IconButton from '@material-ui/core/IconButton';
import { getFormattedDate } from '../../services/formatter-service';
import { actions, EDocumentStatus, SignerStatus } from '../../store/application';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { getBackendUrl } from '../../utils/getBackendUrl';
import { UpdateApplicationConsentActionEnum } from '../../apis/application';
import CircularProgress from '@material-ui/core/CircularProgress';

type ApplicationConsentSectionProps = {
  isReadOnly: boolean;
  applicationUuid: string;
  applicationConsentDocuments: ApplicationConsentDocumentType[];
};

export function ApplicationConsentSection({
  applicationConsentDocuments,
  applicationUuid,
  isReadOnly,
}: ApplicationConsentSectionProps) {
  const intl = useIntl();
  const classes = useStyles();
  const [docsInProgress, setDocsInProgress] = React.useState<string[]>([]);
  const dispatch = useDispatch();
  const state = useSelector((state: AppStoreType) => state);
  const [loading, setLoading] = useState(false);
  const signerStatusToUIStatus = (status: SignerStatus) => {
    switch (status) {
      case SignerStatus.DECLINED:
        return intl.formatMessage({ id: 'application.consent.status.declined' });
      case SignerStatus.DOCUMENT_VIEWED:
        return intl.formatMessage({ id: 'application.consent.status.viewed' });
      case SignerStatus.PENDING:
        return intl.formatMessage({ id: 'application.consent.status.sent' });
      case SignerStatus.NOT_SENT:
        return '';
      case SignerStatus.SIGNED:
        return intl.formatMessage({ id: 'application.consent.status.signed' });
    }
  };
  const documentStatusToUIStatus = (status: EDocumentStatus) => {
    switch (status) {
      case EDocumentStatus.SIGNED:
        return intl.formatMessage({ id: 'application.consent.status.completed' });
      case EDocumentStatus.PENDING:
        return intl.formatMessage({ id: 'application.consent.status.pending' });
      case EDocumentStatus.CANCELED:
        return intl.formatMessage({ id: 'application.consent.status.canceled' });
      case EDocumentStatus.DECLINED:
        return intl.formatMessage({ id: 'application.consent.status.declined' });
    }
  };
  const signerStatusToUIStyle = (status: SignerStatus): ConsentChipStyleEnum => {
    switch (status) {
      case SignerStatus.DECLINED:
        return ConsentChipStyleEnum.ERROR;
      case SignerStatus.DOCUMENT_VIEWED:
        return ConsentChipStyleEnum.PRIMARY;
      case SignerStatus.PENDING:
        return ConsentChipStyleEnum.DEFAULT;
      case SignerStatus.NOT_SENT:
        return ConsentChipStyleEnum.DEFAULT;
      case SignerStatus.SIGNED:
        return ConsentChipStyleEnum.SUCCESS;
    }
  };
  const documentStatusToUIStyle = (status: EDocumentStatus): ConsentChipStyleEnum => {
    switch (status) {
      case EDocumentStatus.SIGNED:
        return ConsentChipStyleEnum.SUCCESS;
      case EDocumentStatus.PENDING:
        return ConsentChipStyleEnum.DEFAULT;
      case EDocumentStatus.CANCELED:
        return ConsentChipStyleEnum.ERROR;
      case EDocumentStatus.DECLINED:
        return ConsentChipStyleEnum.ERROR;
    }
  };

  const startEsign = (uuid: string, lang: 'EN' | 'FR') => async () => {
    setLoading(true);
    const response = await actions.updateApplicationConsent(
      applicationUuid,
      uuid,
      UpdateApplicationConsentActionEnum.START_ESIGN,
      lang,
    )(dispatch, () => state);

    setLoading(false);

    return response;
  };

  const cancelEsign = (uuid: string) => {
    return actions.updateApplicationConsent(
      applicationUuid,
      uuid,
      UpdateApplicationConsentActionEnum.CANCEL_ESIGN,
    )(dispatch, () => state);
  };
  const revertToPending = (uuid: string) => {
    return actions.updateApplicationConsent(
      applicationUuid,
      uuid,
      UpdateApplicationConsentActionEnum.REVERT_TO_PENDING,
    )(dispatch, () => state);
  };

  const getDropdownOptions = (appDocument: ApplicationConsentDocumentType) => {
    const downloadSignedMenuOption = {
      label: <div>{intl.formatMessage({ id: 'application.consent.option.download_signed' })}</div>,
      handler: () => {
        const downloadUrl =
          getBackendUrl() + `/application/${applicationUuid}/documents/${appDocument.uuid}/file`;
        return window.open(downloadUrl, '_blank');
      },
    };
    const downloadBlankMenuOptionEn = {
      label: (
        <div>{intl.formatMessage({ id: 'application.consent.option.download_blank_en' })}</div>
      ),
      handler: () => {
        const downloadUrl =
          getBackendUrl() +
          `/application/${applicationUuid}/documents/${appDocument.uuid}/file?blank=true&lang=EN`;
        return window.open(downloadUrl, '_blank');
      },
    };
    const downloadBlankMenuOptionFr = {
      label: (
        <div>{intl.formatMessage({ id: 'application.consent.option.download_blank_fr' })}</div>
      ),
      handler: () => {
        const downloadUrl =
          getBackendUrl() +
          `/application/${applicationUuid}/documents/${appDocument.uuid}/file?blank=true&lang=FR`;
        return window.open(downloadUrl, '_blank');
      },
    };
    const uploadSignedMenuOption = {
      label: <div>{intl.formatMessage({ id: 'application.consent.option.upload_signed' })}</div>,
      handler: () => {
        setTimeout(() => (document.getElementById(appDocument.uuid) as HTMLInputElement).click());
      },
    };
    const cancelEsignProcessMenuOption = {
      label: <div>{intl.formatMessage({ id: 'application.consent.option.cancel_esign' })}</div>,
      handler: () => {
        setDocsInProgress([...docsInProgress, appDocument.uuid]);
        cancelEsign(appDocument.uuid).finally(() => {
          setDocsInProgress([...docsInProgress.filter((d) => d !== appDocument.uuid)]);
        });
      },
    };
    const reEsignProcessMenuOptionEn = {
      label: <div>{intl.formatMessage({ id: 'application.consent.option.re_esign_en' })}</div>,
      handler: () => {
        setDocsInProgress([...docsInProgress, appDocument.uuid]);
        startEsign(appDocument.uuid, 'EN')().finally(() => {
          setDocsInProgress([...docsInProgress.filter((d) => d !== appDocument.uuid)]);
        });
      },
    };
    const reEsignProcessMenuOptionFr = {
      label: <div>{intl.formatMessage({ id: 'application.consent.option.re_esign_fr' })}</div>,
      handler: () => {
        setDocsInProgress([...docsInProgress, appDocument.uuid]);
        startEsign(appDocument.uuid, 'FR')().finally(() => {
          setDocsInProgress([...docsInProgress.filter((d) => d !== appDocument.uuid)]);
        });
      },
    };
    const revertToPendingMenuOption = {
      label: (
        <div>{intl.formatMessage({ id: 'application.consent.option.revert_to_pending' })}</div>
      ),
      handler: () => {
        setDocsInProgress([...docsInProgress, appDocument.uuid]);
        revertToPending(appDocument.uuid).finally(() => {
          setDocsInProgress([...docsInProgress.filter((d) => d !== appDocument.uuid)]);
        });
      },
    };

    switch (appDocument.documentStatus) {
      case EDocumentStatus.SIGNED:
        if (isReadOnly) {
          return [downloadSignedMenuOption];
        }
        return [
          downloadSignedMenuOption,
          downloadBlankMenuOptionEn,
          downloadBlankMenuOptionFr,
          revertToPendingMenuOption,
        ];
      case EDocumentStatus.DECLINED:
      case EDocumentStatus.CANCELED:
      case EDocumentStatus.PENDING:
        if (isReadOnly) {
          return [];
        }
        const cancelOption = [SignerStatus.PENDING, SignerStatus.DOCUMENT_VIEWED].includes(
          appDocument.signerStatus,
        )
          ? [cancelEsignProcessMenuOption]
          : appDocument.signerStatus === SignerStatus.DECLINED
          ? [reEsignProcessMenuOptionEn, reEsignProcessMenuOptionFr]
          : [];

        return [
          uploadSignedMenuOption,
          downloadBlankMenuOptionEn,
          downloadBlankMenuOptionFr,
          ...cancelOption,
        ];
    }

    return [];
  };

  const getOnFile = (uuid: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (!file) {
      return null;
    }

    setDocsInProgress([...docsInProgress, uuid]);

    actions
      .uploadSignedApplicationConsent(
        applicationUuid,
        uuid,
        file,
      )(dispatch, () => state)
      .finally(() => {
        setDocsInProgress([...docsInProgress.filter((d) => d !== uuid)]);
      });
  };

  return (
    <Paper variant="outlined" className={clsx(classes.paper, classes.consentContainer)}>
      <div className={classes.consentHeaderSection}>
        <Typography variant="h2">
          <b>{intl.formatMessage({ id: 'application.consent.header.main' })}</b>
        </Typography>
      </div>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.consentTableHeaders}>
                {intl.formatMessage({ id: 'application.consent.header.name' })}
              </TableCell>
              <TableCell className={classes.consentTableHeaders}>
                {intl.formatMessage({ id: 'application.consent.header.e_signature_status' })}
              </TableCell>
              <TableCell className={classes.consentTableHeaders}>
                {intl.formatMessage({ id: 'application.consent.header.consent_status' })}
              </TableCell>
              <TableCell width="10px" className={classes.consentTableHeaders} />
            </TableRow>
          </TableHead>
          <TableBody>
            {applicationConsentDocuments.map((i) => (
              <TableRow key={i.uuid}>
                <TableCell className={classes.consentTableCells}>
                  {i.firstName} {i.lastName}
                </TableCell>
                <TableCell className={classes.consentTableCells}>
                  {i.signerStatus !== SignerStatus.NOT_SENT ||
                  i.documentStatus === EDocumentStatus.SIGNED ? (
                    <ConsentChip
                      label={signerStatusToUIStatus(i.signerStatus)}
                      helper={getFormattedDate(i.updatedAt)}
                      style={signerStatusToUIStyle(i.signerStatus)}
                    />
                  ) : (
                    <DropDown
                      disabled={isReadOnly || loading}
                      options={[
                        {
                          label: intl.formatMessage({ id: 'common.english' }),
                          handler: startEsign(i.uuid, 'EN'),
                        },
                        {
                          label: intl.formatMessage({ id: 'common.french' }),
                          handler: startEsign(i.uuid, 'FR'),
                        },
                      ]}
                    >
                      <Button disabled={isReadOnly || loading} color="primary" variant="contained">
                        {intl.formatMessage({ id: 'button.send' })}
                      </Button>
                    </DropDown>
                  )}
                </TableCell>
                <TableCell className={classes.consentTableCells}>
                  <ConsentChip
                    label={documentStatusToUIStatus(i.documentStatus)}
                    style={documentStatusToUIStyle(i.documentStatus)}
                  />
                </TableCell>
                <TableCell className={classes.consentTableCells}>
                  <input id={i.uuid} hidden type="file" onChange={getOnFile(i.uuid)} />
                  {getDropdownOptions(i)?.length > 0 && (
                    <DropDown options={getDropdownOptions(i)}>
                      <IconButton size="small">
                        {!docsInProgress.includes(i.uuid) ? (
                          <KeyboardArrowDownIcon />
                        ) : (
                          <CircularProgress size={24} thickness={5} />
                        )}
                      </IconButton>
                    </DropDown>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
